import { call, CallEffect, select } from 'redux-saga/effects'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { selectors } from '../redux'
import { Recipe } from '../types/fam-graphql'

export const saveJsonCache = async (
  cacheName: string,
  url: string,
  data: any
) => {
  const json = new Response(JSON.stringify(data))
  return saveCache(cacheName, url, json)
}

export const saveCache = async (cacheName: string, url: string, data: any) => {
  return caches.open(cacheName).then((cache) => cache.put(url, data))
}

export const deleteCache = async (cacheName: string) => {
  return caches.delete(cacheName)
}

export const getCacheEntry = async <T = any>(
  cacheName: string,
  url: string
): Promise<T> => {
  return new Promise((resolve) => {
    caches
      .open(cacheName)
      .then((cache) => cache.match(url))
      .then((response: any) => resolve(response))
  })
}

export const getCacheEntries = async (
  cacheName: string
): Promise<Response[]> => {
  return new Promise((resolve) => {
    caches
      .open(cacheName)
      .then((cache) => cache.matchAll())
      .then((responses) => resolve([...responses]))
  })
}

export const getJsonCacheEntries = async <T = any>(
  cacheName: string
): Promise<T[]> => {
  return new Promise((resolve) => {
    getCacheEntries(cacheName).then((responses) => {
      Promise.all(
        responses.map((cachedElement) => cachedElement.json())
      ).then((cachedResources) => resolve(cachedResources))
    })
  })
}

export const getJsonCacheEntry = async <T = any>(
  cacheName: string,
  url: string
): Promise<T> => {
  return new Promise((resolve) => {
    getCacheEntry(cacheName, url).then((response: any) =>
      resolve(response?.json?.())
    )
  })
}

export const getImageCacheEntry = (
  cacheName: string,
  url: string
): Promise<string> => {
  return new Promise((resolve) => {
    getCacheEntry(cacheName, url)
      .then((response: any) => response?.blob())
      .then((blob: any) => resolve(URL.createObjectURL(blob)))
  })
}

export enum CacheType {
  image = 'image',
  data = 'data',
  page = 'page',
}

export const CacheNames = {
  [CacheType.image]: 'offline_images',
  [CacheType.data]: 'offline_data',
  [CacheType.page]: 'offline_pages',
}
export type Loadable = {
  type: CacheType
  url: string
  progress?: number
}

export function* offlineQuery(
  url: string,
  query: () => CallEffect,
  autoCache?: boolean
): any {
  const isOffline = yield select(selectors.app.isOffline)
  console.log('query', { url, isOffline, isBrowser: process.browser })
  if (isOffline && process.browser) {
    console.log('get recipe from cache', url)
    return {
      data: yield call(getJsonCacheEntry, CacheNames[CacheType.data], url),
    }
  } else {
    console.log('load recipe', url)
    const rs = yield query()
    if (process.browser && autoCache) {
      yield call(saveJsonCache, CacheNames[CacheType.data], url, rs?.data)
    }
    return rs
  }
}

export function useCacheImage(url: string) {
  const [image, setImage] = useState('')
  const isOffline = useSelector(selectors.app.isOffline)
  useEffect(() => {
    if (url && isOffline) {
      getImageCacheEntry(CacheNames[CacheType.image], url).then((img) =>
        setImage(img)
      )
    } else {
      setImage('')
    }
  }, [url, isOffline])

  return image
}

export function useCacheImages(urls?: string[]) {
  const [images, setImages] = useState<{ [key: string]: string }>({})
  const isOffline = useSelector(selectors.app.isOffline)
  useEffect(() => {
    if (urls && isOffline) {
      urls?.map((url) => {
        getImageCacheEntry(CacheNames[CacheType.image], url).then((img) =>
          setImages((before) => ({ ...before, [url]: img }))
        )
      })
    }
  }, [urls, isOffline])

  return images
}

export function useOfflineRecipe(recipe?: Recipe): Recipe | undefined | null {
  const isOffline = useSelector(selectors.app.isOffline)
  const image = useCacheImage(recipe?.image ?? '')
  const offlineRecipe = useMemo(() => ({ ...(recipe as Recipe), image }), [
    recipe,
    image,
  ])

  if (isOffline) {
    return offlineRecipe
  }

  return recipe
}

export function useOfflineRecipes(
  recipes?: Recipe[] | null
): Recipe[] | undefined | null {
  const isOffline = useSelector(selectors.app.isOffline)
  const imagesToCache = useMemo(
    () => recipes?.map((r) => r?.image ?? '') ?? [],
    [recipes]
  )
  const images = useCacheImages(imagesToCache)

  const offlineRecipes = useMemo(
    () =>
      recipes?.map((recipe) => ({
        ...recipe,
        image: images[recipe?.image ?? ''],
      })),
    [recipes, images]
  )

  if (isOffline) {
    return offlineRecipes
  }

  return recipes
}
